<template>
  <div class="wallet-detail-container">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Portefeuille: {{ wallet ? wallet.supplier_name : '' }}</h1>
        <p class="subtitle">
          {{ wallet ? wallet.supplier_email : '' }}
        </p>
      </div>
      <router-link
        to="/wallets"
        class="btn btn-secondary"
      >
        <i class="fas fa-arrow-left" /> Retour
      </router-link>
    </div>

    <div
      v-if="wallet"
      class="wallet-content"
    >
      <!-- Balance Cards -->
      <div class="balance-grid">
        <div class="balance-card pending">
          <div class="balance-label">
            En Attente (Incoming)
          </div>
          <div class="balance-amount">
            {{ formatAmount(wallet.pending_balance) }} DT
          </div>
          <div class="balance-info">
            <i class="fas fa-clock" /> Depuis paiements validés
          </div>
        </div>

        <div class="balance-card confirmed">
          <div class="balance-label">
            Confirmé
          </div>
          <div class="balance-amount">
            {{ formatAmount(wallet.confirmed_balance) }} DT
          </div>
          <div class="balance-info">
            <i class="fas fa-check-circle" /> Factures validées
          </div>
        </div>

        <div class="balance-card paid">
          <div class="balance-label">
            Déjà Payé
          </div>
          <div class="balance-amount">
            {{ formatAmount(wallet.paid_balance) }} DT
          </div>
          <div class="balance-info">
            <i class="fas fa-money-bill-wave" /> Paiements effectués
          </div>
        </div>

        <div class="balance-card available highlight">
          <div class="balance-label">
            Disponible à Payer
          </div>
          <div class="balance-amount">
            {{ formatAmount(wallet.available_balance) }} DT
          </div>
          <div class="balance-info">
            <i class="fas fa-wallet" /> Confirmé - Payé
          </div>
        </div>
      </div>

      <!-- Pending Transactions -->
      <div
        v-if="wallet.pending_transactions && wallet.pending_transactions.length > 0"
        class="section"
      >
        <h3>
          <i class="fas fa-clock" /> Transactions En Attente
          <span class="badge">{{ wallet.pending_transactions.length }}</span>
        </h3>
        <div class="transactions-list">
          <div
            v-for="tx in wallet.pending_transactions"
            :key="tx.id"
            class="transaction-item pending"
          >
            <div class="tx-main">
              <div class="tx-reference">
                {{ tx.reference }}
              </div>
              <div class="tx-type">
                {{ tx.type_display }}
              </div>
            </div>
            <div class="tx-details">
              <span class="tx-orders">{{ tx.orders_count }} commandes</span>
              <span class="tx-date">{{ formatDate(tx.created_at) }}</span>
            </div>
            <div class="tx-amount">
              {{ formatAmount(tx.amount) }} DT
            </div>
            <router-link
              :to="`/wallets/transactions/${tx.id}`"
              class="btn-view"
            >
              <i class="fas fa-eye" />
            </router-link>
          </div>
        </div>
      </div>

      <!-- Recent Transactions -->
      <div class="section">
        <div class="section-header">
          <h3>
            <i class="fas fa-history" /> Transactions Récentes
          </h3>
          <router-link
            :to="`/wallets/transactions?supplier=${wallet.supplier_id}`"
            class="btn btn-sm btn-outline-primary"
          >
            Voir tout
          </router-link>
        </div>
        <div
          v-if="wallet.recent_transactions && wallet.recent_transactions.length > 0"
          class="transactions-list"
        >
          <div
            v-for="tx in wallet.recent_transactions"
            :key="tx.id"
            class="transaction-item"
            :class="tx.status"
          >
            <div class="tx-main">
              <div class="tx-reference">
                {{ tx.reference }}
              </div>
              <span
                class="status-badge"
                :class="tx.status"
              >
                {{ tx.status_display }}
              </span>
            </div>
            <div class="tx-details">
              <span class="tx-orders">{{ tx.orders_count }} commandes</span>
              <span class="tx-date">{{ formatDate(tx.created_at) }}</span>
            </div>
            <div class="tx-amount">
              {{ formatAmount(tx.amount) }} DT
            </div>
            <router-link
              :to="`/wallets/transactions/${tx.id}`"
              class="btn-view"
            >
              <i class="fas fa-eye" />
            </router-link>
          </div>
        </div>
        <div
          v-else
          class="empty-state"
        >
          <p>Aucune transaction</p>
        </div>
      </div>
    </div>

    <div
      v-else-if="loading"
      class="loading-state"
    >
      <i class="fas fa-spinner fa-spin" />
      <p>Chargement...</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'WalletShow',
  data() {
    return {
      wallet: null,
      loading: false,
    }
  },
  created() {
    this.loadWallet()
  },
  methods: {
    async loadWallet() {
      this.loading = true
      try {
        const response = await axios.get(`/api/wallets/${this.$route.params.id}/`)
        this.wallet = response.data
      } catch (error) {
        this.$toast.error('Erreur lors du chargement du portefeuille')
      } finally {
        this.loading = false
      }
    },

    formatAmount(amount) {
      if (!amount) return '0.000'
      return parseFloat(amount).toFixed(3)
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.wallet-detail-container {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }

  .subtitle {
    color: #64748b;
    margin: 0.25rem 0 0;
  }
}

.balance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.balance-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .balance-label {
    font-size: 0.875rem;
    color: #64748b;
    margin-bottom: 0.5rem;
  }

  .balance-amount {
    font-size: 1.75rem;
    font-weight: 700;
    font-family: 'Monaco', monospace;
  }

  .balance-info {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-top: 0.5rem;

    i { margin-right: 0.25rem; }
  }

  &.pending {
    border-left: 4px solid #f59e0b;
    .balance-amount { color: #f59e0b; }
  }

  &.confirmed {
    border-left: 4px solid #10b981;
    .balance-amount { color: #10b981; }
  }

  &.paid {
    border-left: 4px solid #8b5cf6;
    .balance-amount { color: #8b5cf6; }
  }

  &.available.highlight {
    border-left: 4px solid #6366f1;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;

    .balance-label, .balance-info { color: rgba(255,255,255,0.8); }
    .balance-amount { color: white; }
  }
}

.section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .badge {
      background: #e0e7ff;
      color: #6366f1;
      padding: 0.125rem 0.5rem;
      border-radius: 10px;
      font-size: 0.75rem;
    }
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    h3 { margin: 0; }
  }
}

.transactions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.transaction-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 3px solid #e2e8f0;

  &.pending { border-left-color: #f59e0b; }
  &.confirmed { border-left-color: #10b981; }
  &.paid { border-left-color: #8b5cf6; }

  .tx-main {
    flex: 1;

    .tx-reference {
      font-weight: 600;
      color: #1e293b;
    }

    .tx-type {
      font-size: 0.75rem;
      color: #64748b;
    }
  }

  .tx-details {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.75rem;
    color: #64748b;
  }

  .tx-amount {
    font-size: 1.125rem;
    font-weight: 700;
    font-family: 'Monaco', monospace;
    color: #1e293b;
    min-width: 120px;
    text-align: right;
  }

  .btn-view {
    padding: 0.5rem;
    color: #64748b;
    border-radius: 6px;

    &:hover {
      background: #e2e8f0;
      color: #6366f1;
    }
  }
}

.status-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;

  &.pending {
    background: #fef3c7;
    color: #f59e0b;
  }

  &.confirmed {
    background: #d1fae5;
    color: #10b981;
  }

  &.paid {
    background: #ede9fe;
    color: #8b5cf6;
  }
}

.empty-state, .loading-state {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;

  &.btn-secondary {
    background: #f1f5f9;
    color: #64748b;
    border: none;
    &:hover { background: #e2e8f0; }
  }

  &.btn-outline-primary {
    background: white;
    color: #6366f1;
    border: 1px solid #6366f1;
    &:hover { background: #f0f0ff; }
  }

  &.btn-sm {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
}
</style>
